import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { LlmProvider, LlmProviderCreate } from '../../types';
import { llmProviderApi } from '../../services/api';
import { ErrorAlert } from '../common';

interface LlmProviderFormProps {
  open: boolean;
  provider: LlmProvider | null;
  onClose: () => void;
  onSuccess: () => void;
}

const LlmProviderForm: React.FC<LlmProviderFormProps> = ({
  open,
  provider,
  onClose,
  onSuccess,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isEdit = !!provider;
  const { control, handleSubmit, formState: { errors } } = useForm<LlmProviderCreate>({
    defaultValues: provider ? {
      name: provider.name,
      type: provider.type,
      api_key: '',
      is_active: provider.is_active,
      config: provider.config,
    } : {
      name: '',
      type: 'openai',
      api_key: '',
      is_active: true,
      config: {},
    },
  });

  const mutation = useMutation({
    mutationFn: (data: LlmProviderCreate) =>
      isEdit ? llmProviderApi.update(provider.id, data) : llmProviderApi.create(data),
    onSuccess: () => {
      onSuccess();
    },
  });

  const onSubmit = (data: LlmProviderCreate) => {
    mutation.mutate(data);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ py: isMobile ? 1.5 : 2 }}>{isEdit ? 'Edit Provider' : 'Add Provider'}</DialogTitle>
      <DialogContent sx={{ px: isMobile ? 2 : 3 }}>
        <Box component="form" sx={{ pt: 2 }}>
          <Controller
            name="name"
            control={control}
            rules={{ required: 'Name is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Name"
                fullWidth
                margin="normal"
                error={!!errors.name}
                helperText={errors.name?.message}
                InputProps={{ style: { fontSize: 16 } }}
              />
            )}
          />

          <Controller
            name="type"
            control={control}
            rules={{ required: 'Type is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                select
                label="Provider Type"
                fullWidth
                margin="normal"
                error={!!errors.type}
                helperText={errors.type?.message}
              >
                <MenuItem value="openai">OpenAI</MenuItem>
                <MenuItem value="anthropic">Anthropic</MenuItem>
                <MenuItem value="google">Google</MenuItem>
                <MenuItem value="azure">Azure</MenuItem>
                <MenuItem value="cohere">Cohere</MenuItem>
                <MenuItem value="huggingface">Hugging Face</MenuItem>
                <MenuItem value="xai">xAI (Grok)</MenuItem>
                <MenuItem value="openrouter">OpenRouter</MenuItem>
                <MenuItem value="deepseek">DeepSeek</MenuItem>
                <MenuItem value="kimi">Kimi</MenuItem>
                <MenuItem value="minimax">MiniMax</MenuItem>
                <MenuItem value="ollama">Ollama</MenuItem>
                <MenuItem value="vllm">vLLM</MenuItem>
                <MenuItem value="echo">Echo (Testing)</MenuItem>
              </TextField>
            )}
          />

          <Controller
            name="api_key"
            control={control}
            rules={!isEdit ? { required: 'API Key is required' } : {}}
            render={({ field }) => (
              <TextField
                {...field}
                label="API Key"
                type="password"
                fullWidth
                margin="normal"
                error={!!errors.api_key}
                helperText={errors.api_key?.message || (isEdit ? 'Leave empty to keep current key' : '')}
              />
            )}
          />

          <Controller
            name="is_active"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Switch {...field} checked={field.value} />}
                label="Active"
                sx={{ mt: 2 }}
              />
            )}
          />

          {mutation.error && (
            <ErrorAlert error={mutation.error as Error} />
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ 
        px: isMobile ? 2 : 3, 
        py: isMobile ? 1.5 : 2,
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 1 : 0,
        '& > button': { width: isMobile ? '100%' : 'auto' }
      }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          disabled={mutation.isPending}
        >
          {mutation.isPending ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LlmProviderForm;
